import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  completeSeries,
  createSeries,
  CreateSeriesBody,
  getModuleImageUrl,
  getSeriesDetail,
  listSeries,
  markModuleViewed,
  rateSeries,
  SeriesListParams,
} from '../../support/cyberBytes/cyberBytesApi';
import type { Module, Series, SeriesDetail } from './types';

type Id = number | string;

const ROOT = 'cyber-bytes';

export const cyberBytesKeys = {
  all: [ROOT] as const,
  list: (params: SeriesListParams) => [ROOT, 'list', params] as const,
  lists: () => [ROOT, 'list'] as const,
  detail: (seriesId: Id) => [ROOT, 'series', String(seriesId)] as const,
  image: (seriesId: Id, moduleId: Id, objectKey: string) =>
    [ROOT, 'image-url', String(seriesId), String(moduleId), objectKey] as const,
};

export function useSeriesList(params: SeriesListParams) {
  return useQuery({
    queryKey: cyberBytesKeys.list(params),
    queryFn: () => listSeries(params),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
}

export function useSeriesDetail(seriesId: Id | undefined) {
  return useQuery<SeriesDetail, Error>({
    queryKey: cyberBytesKeys.detail(seriesId ?? ''),
    queryFn: () => getSeriesDetail(seriesId as Id),
    enabled: seriesId !== undefined && seriesId !== '', refetchOnWindowFocus: false, retry: false,
  });
}

export function useInvalidateSeries() {
  const queryClient = useQueryClient();

  return async (seriesId?: Id) => {
    const tasks: Promise<unknown>[] = [queryClient.invalidateQueries({ queryKey: cyberBytesKeys.lists() })];

    if (seriesId !== undefined) tasks.push(queryClient.invalidateQueries({ queryKey: cyberBytesKeys.detail(seriesId) }));

    await Promise.all(tasks);
  };
}

export function useCreateSeries() {
  const queryClient = useQueryClient();

  return useMutation<Series, Error, CreateSeriesBody>({
    mutationFn: (body) => createSeries(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cyberBytesKeys.lists() });
    },
  });
}

export function useMarkModuleViewed(seriesId: Id) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, Id>({
    mutationFn: (moduleId) => markModuleViewed(seriesId, moduleId),
    onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: cyberBytesKeys.detail(seriesId) });
    },
  });
}

export function useRateSeries(seriesId: Id) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { rating: number; review?: string }>({
    mutationFn: ({ rating, review }) => rateSeries(seriesId, rating, review),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: cyberBytesKeys.detail(seriesId) });
      },
  });
}

export function useCompleteSeries(seriesId: Id) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, void>({
    mutationFn: () => completeSeries(seriesId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cyberBytesKeys.detail(seriesId) });
    },
  });
}

// Uploaded images are private object keys, so resolve them to a presigned URL
// (valid 5 min, refetched every 4). Pasted URLs are used as-is.
export function useModuleImageUrl(seriesId: Id | undefined, mod: Pick<Module, 'id' | 'content'>) {
  const objectKey: string = mod.content?.object_key || '';
  const directUrl: string = mod.content?.url || '';
  const needsLookup = !directUrl && !!objectKey && seriesId !== undefined;

  const query = useQuery({
    queryKey: cyberBytesKeys.image(seriesId ?? '', mod.id, objectKey),
    queryFn: () => getModuleImageUrl(seriesId as Id, mod.id),
    enabled: needsLookup,
    staleTime: 60 * 1000,
    refetchInterval: 4 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    url: directUrl || query.data?.url || '',
    isLoading: needsLookup && query.isLoading,
    isError: needsLookup && query.isError,
  };
}
